<div class="card">
  <div class="card-content">

    <!-- Stats: shared .stats-cards-row / .stats-card so this page reads like the others -->
    <div class="stats-cards-row">
      <!-- The table scrolls rather than paginating, so this is where the row count lives —
           the same "Showing / After filters" card the Availability report uses. It also
           tells the reader how many people the hour totals beside it are drawn from. -->
      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ shownEmployeeCount }}</span>
          <span class="stats-card-label">Allocated</span>
          <span class="stats-card-sublabel">
            <ng-container *ngIf="shownEmployeeCount !== totalEmployeeCount; else allShown">
              of {{ totalEmployeeCount }} &mdash; filtered
            </ng-container>
            <ng-template #allShown>With planned time</ng-template>
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-users"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatHoursCompact(totalExpectedHours) }}</span>
          <span class="stats-card-label">Expected</span>
          <span class="stats-card-sublabel">
            {{ report?.workingDaysInMonth ?? 0 }} working days &times; {{ report?.standardHoursPerDay ?? 8 }}h
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-calendar"></i></div>
      </div>

      <div class="stats-card stats-card--total">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatHoursCompact(totalActualHours) }}</span>
          <span class="stats-card-label">Actual Logged</span>
          <span class="stats-card-sublabel">Leave excluded</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-clock"></i></div>
      </div>

      <div class="stats-card"
           [class.stats-card--danger]="totalVarianceHours > 0"
           [class.stats-card--warning]="totalVarianceHours < 0"
           [class.stats-card--success]="totalVarianceHours === 0">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ formatVarianceCompact(totalVarianceHours) }}</span>
          <span class="stats-card-label">Variance</span>
          <span class="stats-card-sublabel">Actual &minus; expected</span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-chart-line"></i></div>
      </div>

      <!-- Clickable: narrows the table to the people this card counts. Toggles off on a
           second click, so the card is its own way back. -->
      <div class="stats-card stats-card--success evr-card-clickable"
           [class.evr-card-active]="isStatusFilterActive('OnTrack')"
           role="button" tabindex="0"
           [attr.aria-pressed]="isStatusFilterActive('OnTrack')"
           (click)="toggleStatusFilter('OnTrack')"
           (keydown.enter)="toggleStatusFilter('OnTrack')"
           (keydown.space)="$event.preventDefault(); toggleStatusFilter('OnTrack')"
           pTooltip="Show only employees on track" tooltipPosition="top">
        <div class="stats-card-main">
          <span class="stats-card-value">{{ onTrackCount }}</span>
          <span class="stats-card-label">On Track</span>
          <span class="stats-card-sublabel">
            &plusmn;{{ report?.onTrackTolerancePercentage ?? 10 }}% tolerance
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-check-circle"></i></div>
      </div>

      <!-- Three counts in one card, so each number is its own filter rather than the card
           as a whole — clicking "over" should not also pull in "under". -->
      <div class="stats-card stats-card--warning evr-card-split">
        <div class="stats-card-main">
          <span class="stats-card-value evr-split-value">
            <button type="button" class="evr-split-part evr-split-button"
                    [class.evr-split-active]="isStatusFilterActive('Over')"
                    [attr.aria-pressed]="isStatusFilterActive('Over')"
                    (click)="toggleStatusFilter('Over')"
                    pTooltip="Show only employees who logged more than planned" tooltipPosition="top">
              <span class="evr-split-num evr-split-over">{{ overCount }}</span>
              <span class="evr-split-tag">over</span>
            </button>
            <span class="evr-split-sep">&middot;</span>
            <button type="button" class="evr-split-part evr-split-button"
                    [class.evr-split-active]="isStatusFilterActive('Under')"
                    [attr.aria-pressed]="isStatusFilterActive('Under')"
                    (click)="toggleStatusFilter('Under')"
                    pTooltip="Show only employees who logged less than planned" tooltipPosition="top">
              <span class="evr-split-num evr-split-under">{{ underCount }}</span>
              <span class="evr-split-tag">under</span>
            </button>
            <ng-container *ngIf="notLoggedInViewCount > 0 || isNotLoggedFilterActive">
              <span class="evr-split-sep">&middot;</span>
              <button type="button" class="evr-split-part evr-split-button"
                      [class.evr-split-active]="isNotLoggedFilterActive"
                      [attr.aria-pressed]="isNotLoggedFilterActive"
                      (click)="toggleNotLoggedFilter()"
                      pTooltip="Show only employees with no work log for this month" tooltipPosition="top">
                <span class="evr-split-num evr-split-notlogged">{{ notLoggedInViewCount }}</span>
                <span class="evr-split-tag">not logged</span>
              </button>
            </ng-container>
          </span>
          <span class="stats-card-label">Outside Tolerance</span>
          <span class="stats-card-sublabel">
            vs. planned<span *ngIf="notLoggedInViewCount > 0">, or no work log</span>
          </span>
        </div>
        <div class="stats-card-icon"><i class="pi pi-exclamation-triangle"></i></div>
      </div>
    </div>

    <!-- Filters: shared .filter-bar, shown/hidden by the main header filter toggle -->
    <div class="filter-bar filter-bar-collapsible mb-4"
         [class.filter-bar-visible]="pageHeaderService.currentFiltersVisible">
      <div class="filter-bar-filters">
        <div class="filter-field">
          <label for="evr-year" class="filter-label">Year</label>
          <p-select id="evr-year" [options]="yearOptions" [(ngModel)]="selectedYear"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="evr-month" class="filter-label">Month</label>
          <p-select id="evr-month" [options]="monthOptions" [(ngModel)]="selectedMonth"
                    optionLabel="label" optionValue="value" (ngModelChange)="onFilterChange()"
                    appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="evr-project" class="filter-label">Project</label>
          <p-multiSelect id="evr-project" [options]="projectOptions" [(ngModel)]="selectedProjectIds"
                         optionLabel="label" optionValue="value" placeholder="All"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         [showToggleAll]="true" [filter]="true"
                         filterPlaceholder="Search project..."
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field">
          <label for="evr-status" class="filter-label">Variance</label>
          <p-multiSelect id="evr-status" [options]="statusOptions" [(ngModel)]="selectedStatuses"
                         (ngModelChange)="onStatusFilterChange()"
                         optionLabel="label" optionValue="value" placeholder="All"
                         [maxSelectedLabels]="1" selectedItemsLabel="{0} selected"
                         panelStyleClass="filter-dropdown-panel" appendTo="body" />
        </div>

        <div class="filter-field evr-filter-toggle">
          <label for="evr-drafts" class="filter-label">Include Drafts</label>
          <div class="evr-toggle-cell">
            <p-toggleSwitch inputId="evr-drafts" [(ngModel)]="includeDrafts"
                            (onChange)="onFilterChange()"
                            pTooltip="Count hours from work logs that have not been submitted yet"
                            tooltipPosition="top" />
          </div>
        </div>

        <!-- People with no plan this month are not rows in the variance table — there is no
             expected figure to measure an actual against. The count is kept on the filter row
             so the gap between this report and the roster stays visible, and the list opens
             in a dialog with the room to read it. -->
        <div class="filter-field evr-filter-unallocated"
             *ngIf="(report?.unallocatedEmployees?.length ?? 0) > 0">
          <label class="filter-label">&nbsp;</label>
          <button type="button" class="btn-unallocated-view" (click)="openUnallocatedDialog()"
                  pTooltip="Employees with no planned time this month" tooltipPosition="top">
            <i class="pi pi-eye" aria-hidden="true"></i>
            Not Allocated ({{ report?.unallocatedEmployees?.length ?? 0 }})
          </button>
        </div>

        <div class="filter-field evr-filter-clear" *ngIf="hasActiveFilters">
          <label class="filter-label">&nbsp;</label>
          <button type="button" class="btn-clear-filters" (click)="clearFilters()"
                  pTooltip="Clear all filters" tooltipPosition="top" title="Clear all filters">
            <i class="pi pi-filter-slash"></i>
          </button>
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="loading-container loading-container--centered">
      <i class="pi pi-spin pi-spinner loading-spinner-icon"></i>
      <p>Loading effort variance report...</p>
    </div>

    <ng-container *ngIf="!loading && report">

      <p-table [value]="allEmployees" styleClass="evr-table resize-handles-visible"
               [tableStyle]="{ 'min-width': '100%' }" [paginator]="false" [scrollable]="true"
               scrollHeight="flex" sortMode="multiple"
               [resizableColumns]="true" columnResizeMode="expand"
               persistColumnWidths="evr-main">

        <ng-template pTemplate="header">
          <tr>
            <!-- No resizer on the expand toggle: a grip would crowd an icon-width column. -->
            <th class="evr-th-toggle"></th>
            <th pResizableColumn [pSortableColumn]="'employeeName'">Employee <p-sortIcon field="employeeName" /></th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'totalAllocationPercentage'">
              Alloc % <p-sortIcon field="totalAllocationPercentage" />
            </th>
            <th pResizableColumn class="text-right">Work Days</th>
            <th pResizableColumn class="text-right">Leave</th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'expectedHours'">
              Expected <p-sortIcon field="expectedHours" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'actualHours'">
              Actual <p-sortIcon field="actualHours" />
            </th>
            <th pResizableColumn class="text-right" [pSortableColumn]="'varianceHours'">
              Variance <p-sortIcon field="varianceHours" />
            </th>
            <th pResizableColumn class="text-right"
                pTooltip="Variance as a percentage of expected hours. +50% means half again more than planned; -50% means half the planned effort."
                tooltipPosition="top">Var % of Plan</th>
            <th pResizableColumn class="text-center">Status</th>
            <!-- Last column: dragging the final border has nothing to push against. -->
            <th class="text-center">Work Log</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-employee>
          <!-- Employee total -->
          <tr class="evr-employee-row" [class.evr-row-expanded]="isExpanded(employee.employeeId)"
              [class.evr-row-not-logged]="isNotLogged(employee)"
              (click)="toggleRow(employee.employeeId)">
            <td class="evr-td-toggle">
              <button type="button" class="evr-toggle-btn"
                      (click)="toggleRow(employee.employeeId); $event.stopPropagation()"
                      [attr.aria-expanded]="isExpanded(employee.employeeId)"
                      [attr.aria-label]="'Show projects for ' + employee.employeeName">
                <i class="pi" [ngClass]="isExpanded(employee.employeeId) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              </button>
            </td>
            <td>
              <div class="evr-employee-cell">
                <span class="evr-employee-name">{{ employee.employeeName }}</span>
                <span class="evr-employee-meta">{{ employee.projects.length }} project(s)</span>
              </div>
            </td>
            <td class="text-right evr-num" [class.evr-over-allocated]="employee.totalAllocationPercentage > 100">
              {{ employee.totalAllocationPercentage | number:'1.0-1' }}%
            </td>
            <td class="text-right evr-num evr-muted">{{ employee.workingDays }}</td>
            <td class="text-right evr-num evr-muted">{{ formatDays(employee.leaveDays) }}</td>
            <td class="text-right evr-num">{{ formatHours(employee.expectedHours) }}</td>
            <td class="text-right evr-num evr-strong">{{ formatHours(employee.actualHours) }}</td>
            <td class="text-right evr-num evr-variance"
                [class.evr-variance-over]="employee.varianceHours > 0"
                [class.evr-variance-under]="employee.varianceHours < 0"
                [class.evr-variance-big]="isSignificantVariance(employee)">
              {{ formatVariance(employee.varianceHours) }}
            </td>
            <td class="text-right evr-num evr-muted">{{ formatPercentage(employee.variancePercentage) }}</td>
            <td class="text-center">
              <p-tag [value]="statusLabel(employee.status)" [severity]="statusSeverity(employee.status)" />
            </td>
            <td class="text-center">
              <p-tag [value]="timesheetStatusLabel(employee.timesheetStatus)"
                     [severity]="timesheetStatusSeverity(employee.timesheetStatus)" />
            </td>
          </tr>

          <!-- Per-project detail: where a single project absorbing extra effort shows up -->
          <ng-container *ngIf="isExpanded(employee.employeeId)">
          <tr *ngFor="let project of employee.projects" class="evr-project-row">
            <td></td>
            <td>
              <div class="evr-project-cell">
                <i class="pi pi-angle-right evr-project-icon"></i>
                <span class="evr-project-name">{{ project.projectName || '(unnamed project)' }}</span>
                <span *ngIf="project.isUnallocatedWork" class="evr-badge-unplanned"
                      pTooltip="Hours logged on a project with no allocation" tooltipPosition="top">
                  unplanned
                </span>
              </div>
            </td>
            <td class="text-right evr-num">{{ project.allocationPercentage | number:'1.0-1' }}%</td>
            <td class="text-right evr-num evr-muted">{{ project.workingDays }}</td>
            <td class="text-right evr-num evr-muted">{{ formatDays(project.leaveDays) }}</td>
            <td class="text-right evr-num">{{ formatHours(project.expectedHours) }}</td>
            <td class="text-right evr-num">{{ formatHours(project.actualHours) }}</td>
            <td class="text-right evr-num evr-variance"
                [class.evr-variance-over]="project.varianceHours > 0"
                [class.evr-variance-under]="project.varianceHours < 0"
                [class.evr-variance-big]="isSignificantVariance(project)">
              {{ formatVariance(project.varianceHours) }}
            </td>
            <td class="text-right evr-num evr-muted">{{ formatPercentage(project.variancePercentage) }}</td>
            <td class="text-center">
              <p-tag [value]="statusLabel(project.status)" [severity]="statusSeverity(project.status)" />
            </td>
            <td></td>
          </tr>
          </ng-container>
        </ng-template>


        <ng-template pTemplate="emptymessage">
          <tr class="table-empty-row">
            <td colspan="11" class="table-empty-cell">
              <div class="empty-state">
                <div class="empty-icon"><i class="pi pi-inbox"></i></div>
                <p class="empty-message">
                  {{ hasActiveFilters ? 'No matching employees' : 'No allocations for this month' }}
                </p>
                <p class="empty-description">
                  {{ hasActiveFilters
                      ? 'Try clearing the filters or searching for a different employee or project'
                      : 'No employees are allocated for ' + monthLabel + ' ' + selectedYear }}
                </p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

    </ng-container>

    <div *ngIf="!loading && !report" class="empty-state">
      <i class="pi pi-exclamation-circle"></i>
      <span>Report could not be loaded.</span>
    </div>

  </div>
</div>

<!-- Not-allocated dialog. Reuses the shared .unallocated-employees-dialog styles from the
     Monthly Allocation workforce popup, which answers the same question from the planning
     side, so the two read as one pattern rather than two lookalike lists. -->
<p-dialog
  [visible]="showUnallocatedDialog"
  (visibleChange)="showUnallocatedDialog = $event"
  [modal]="true"
  styleClass="dialog-workforce dialog-workforce--auto unallocated-employees-dialog"
  [dismissableMask]="true"
  [closable]="true">

  <ng-template pTemplate="header">
    <div class="dialog-header-with-meta">
      <span class="dialog-header-title">Not Allocated</span>
      <span class="dialog-header-badge">{{ unallocatedCount }}</span>
      <span class="dialog-header-period">{{ monthLabel }} {{ selectedYear }}</span>
    </div>
  </ng-template>

  <div class="unallocated-dialog-inner">
    <p class="workforce-tab-hint">
      Active and allocatable, but with no planned time this month. They are not in the
      variance table because there is no plan to measure the logged hours against.
    </p>

    <div class="unallocated-table-scroll-holder">
      <p-table [value]="unallocatedEmployees"
               styleClass="compact-striped-table unallocated-dialog-table resize-handles-visible"
               [paginator]="false" sortMode="multiple"
               [resizableColumns]="true" columnResizeMode="expand"
               persistColumnWidths="evr-unallocated-dialog">
        <ng-template pTemplate="header">
          <tr>
            <th pResizableColumn class="unallocated-col-name" pSortableColumn="employeeName">
              Employee Name <p-sortIcon field="employeeName" />
            </th>
            <th pResizableColumn class="unallocated-col-role" pSortableColumn="roleTitle">
              Role <p-sortIcon field="roleTitle" />
            </th>
            <th pResizableColumn class="unallocated-col-designation" pSortableColumn="designationName">
              Designation <p-sortIcon field="designationName" />
            </th>
            <!-- Last column: sortable, but no resizer — dragging the final border has
                 nothing to push against. -->
            <th class="unallocated-col-service" pSortableColumn="serviceGroupName">
              Service Group <p-sortIcon field="serviceGroupName" />
            </th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-employee>
          <tr>
            <td class="unallocated-cell-name">
              <span class="unallocated-cell-text" [pTooltip]="employee.employeeName"
                    tooltipPosition="top">{{ employee.employeeName }}</span>
            </td>
            <td class="unallocated-cell-role">
              <span class="unallocated-cell-text">{{ employee.roleTitle || '—' }}</span>
            </td>
            <td class="unallocated-cell-designation">
              <span class="unallocated-cell-text">{{ employee.designationName || '—' }}</span>
            </td>
            <td class="unallocated-cell-service">
              <span class="unallocated-cell-text">{{ employee.serviceGroupName || '—' }}</span>
            </td>
          </tr>
        </ng-template>

        <ng-template pTemplate="emptymessage">
          <tr class="table-empty-row table-empty-row--compact">
            <td colspan="4" class="table-empty-cell">
              <div class="empty-state empty-state--compact">
                <div class="empty-icon"><i class="pi pi-inbox"></i></div>
                <p class="empty-message">No matching employees</p>
                <p class="empty-description">Try clearing the search in the page header</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <div class="workforce-footer">
      <span class="workforce-footer-summary">
        <strong>{{ totalEmployeeCount }}</strong> with planned time
        <span class="workforce-footer-sep">&middot;</span>
        <strong>{{ unallocatedCount }}</strong> not allocated
      </span>
      <span class="workforce-footer-actions">
        <p-button label="Close" icon="pi pi-times"
                  (onClick)="closeUnallocatedDialog()" severity="secondary" />
      </span>
    </div>
  </ng-template>
</p-dialog>
